import { supabase } from "../utils/supabaseClient";

// ================================================================
//  reportService — data for the Service / Expense reports.
//  Same queries as the mobile app (app/(home)/reports.tsx).
// ================================================================

const PAGE = 1000; // Supabase returns at most 1000 rows per request

// Pages through a query so long reports are never silently cut at 1000 rows.
// `makeQuery` must return a fresh, fully ordered query each time it is called.
async function fetchAllPages(makeQuery) {
    let all = [];
    let from = 0;
    for (;;) {
        const { data, error } = await makeQuery().range(from, from + PAGE - 1);
        if (error) throw new Error(error.message);
        all = all.concat(data ?? []);
        if (!data || data.length < PAGE) break;
        from += PAGE;
    }
    return all;
}

// Service entries of one partner between two dates (YYYY-MM-DD, inclusive).
export async function fetchServiceReport(partnerId, dateFrom, dateTo, orderByName) {
    const data = await fetchAllPages(() =>
        supabase
            .from("service_entries")
            .select("id, service_date, reason, service_given, pay_mode, amount, gst_amount, net_amount, share_amount, agencies(agency_name)")
            .eq("partner_id", partnerId)
            .gte("service_date", dateFrom)
            .lte("service_date", dateTo + "T23:59:59")
            .order("service_date", { ascending: true })
            .order("id", { ascending: true })
    );

    const rows = data.map((row) => ({
        id: row.id,
        service_date: row.service_date,
        client_name: row.agencies?.agency_name ?? "",
        reason: row.reason ?? "",
        service_given: row.service_given,
        pay_mode: row.pay_mode,
        amount: row.amount ?? 0,
        gst_amount: row.gst_amount ?? 0,
        net_amount: row.net_amount ?? 0,
        share_amount: row.share_amount ?? 0,
    }));

    return orderByName
        ? rows.sort((a, b) => a.client_name.localeCompare(b.client_name))
        : rows;
}

// Expenses between two dates. partnerId 0 / falsy = all partners.
export async function fetchExpenseReport(partnerId, dateFrom, dateTo) {
    const data = await fetchAllPages(() => {
        let query = supabase
            .from("expenses")
            .select("id, expense_date, place, remarks, km, travel, food, toll, partners!expenses_partner_id_fkey(partner_name)")
            .gte("expense_date", dateFrom)
            .lte("expense_date", dateTo)
            .order("expense_date", { ascending: true })
            .order("id", { ascending: true });
        if (partnerId > 0) query = query.eq("partner_id", partnerId);
        return query;
    });

    return data.map((row) => ({
        id: row.id,
        expense_date: row.expense_date,
        place: row.place ?? "",
        remarks: row.remarks ?? "",
        km: row.km ?? 0,
        travel: row.travel ?? 0,
        food: row.food ?? 0,
        toll: row.toll ?? 0,
        partner_name: row.partners?.partner_name ?? "",
    }));
}
